import React, { useState } from "react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const GENDER_OPTIONS = ["female", "male", "other", "prefer_not_to_say"];
export const INTEREST_OPTIONS = [
  "food",
  "health",
  "shopping",
  "property",
  "jobs",
  "education",
  "events",
  "services",
];

const chip = (selected) =>
  `rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
    selected ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border hover:bg-muted"
  }`;

/**
 * `variant="signup"` (default) is the first-run form: the 18+ checkbox lives here and ONLY here.
 * `variant="account"` is the same form on the Account page: 18+ is not shown (sign-up only),
 * the city is text (with "Change city" only when there is more than one), and there is ONE pinned
 * Save that reads "Saved ✓" when nothing is pending - no per-field saves.
 */
export function ProfileForm({ onSaved, onSkip, submitLabel, variant = "signup" }) {
  const account = variant === "account";
  const { t, lang, auth, tenant, tenants, refreshMe } = useApp();
  const identity = auth?.identity || {};
  const genderLabels = t("profile.genderLabels");
  const interestLabels = t("profile.interestLabels");
  const [adultTouched, setAdultTouched] = useState(false);
  const [form, setForm] = useState(() => ({
    display_name: identity.display_name || "",
    gender: identity.gender || "",
    is_adult: identity.is_adult ?? false,
    city_tenant_id: identity.city_tenant_id || tenant?.id || "",
    interests: identity.interests || [],
  }));
  const [initial] = useState(() => JSON.stringify(form));
  const cityName = (() => {
    const c = (tenants || []).find((x) => x.id === form.city_tenant_id) || tenant;
    return c ? (lang === "hi" ? c.name_hi || c.name : c.name) : "";
  })();
  const [busy, setBusy] = useState(false);
  const [changingCity, setChangingCity] = useState(false);
  const [baseline, setBaseline] = useState(null);

  const toggleInterest = (key) =>
    setForm((prev) => ({
      ...prev,
      interests: prev.interests.includes(key)
        ? prev.interests.filter((i) => i !== key)
        : [...prev.interests, key],
    }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/profile", {
        display_name: form.display_name,
        gender: form.gender,
        // 18+ is collected at sign-up only; the Account page neither shows nor re-sends it.
        ...(account ? {} : { is_adult: form.is_adult }),
        city_tenant_id: form.city_tenant_id || null,
        interests: form.interests,
      });
      await refreshMe();
      setBaseline(JSON.stringify(form));
      setChangingCity(false);
      toast.success(t("profile.saved"));
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  // The state the form was last saved (or loaded) in, so the pinned Save knows whether anything is
  // pending. Held in state, not a ref, so "Saved ✓" re-renders the moment it is reached.
  const loaded = JSON.stringify(form);
  const dirty = account && loaded !== (baseline ?? initial);

  const skip = async () => {
    try {
      await api.post("/auth/profile/skip");
    } finally {
      onSkip?.();
    }
  };

  return (
    <div className="space-y-5" data-testid="profile-form">
      <div className="space-y-1.5">
        <Label htmlFor="profile-name">{t("profile.fullName")}</Label>
        <Input
          id="profile-name"
          data-testid="profile-name-input"
          value={form.display_name}
          placeholder={t("profile.fullNamePlaceholder")}
          onChange={(e) => setForm({ ...form, display_name: e.target.value })}
        />
      </div>

      <div className="space-y-2">
        <Label>
          {t("profile.gender")} <span className="font-normal text-muted-foreground">({t("common.optional")})</span>
        </Label>
        <p className="text-xs text-muted-foreground">{t("profile.genderHint")}</p>
        <div className="flex flex-wrap gap-2" data-testid="profile-gender-options">
          {GENDER_OPTIONS.map((g) => (
            <button
              key={g}
              type="button"
              data-testid={`profile-gender-${g}`}
              className={chip(form.gender === g)}
              onClick={() => setForm({ ...form, gender: form.gender === g ? "" : g })}
            >
              {genderLabels[g]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* One bit, not a date of birth. Holding an age would give us actual knowledge of
            minority, which under DPDP s.9 obliges verifiable parental consent we cannot obtain.
            The server rejects a false answer rather than storing it. */}
        {account ? null : (
        <div className="space-y-1.5">
          <label
            htmlFor="profile-is-adult"
            className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3"
          >
            <input
              id="profile-is-adult"
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-[var(--primary)]"
              data-testid="profile-is-adult-input"
              checked={!!form.is_adult}
              onChange={(e) => {
                setAdultTouched(true);
                setForm({ ...form, is_adult: e.target.checked });
              }}
            />
            <span>
              <span className="text-sm font-medium">{t("profile.isAdult")}</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {t("profile.isAdultHint")}
              </span>
            </span>
          </label>
          {/* Said next to the checkbox, before Save, rather than as a toast after a server 4xx
              far from where the person is looking. Never auto-ticked: it is a self-declaration. */}
          {/* R7-5: only after the field is touched - never on first load. */}
          {adultTouched && !form.is_adult && (
            <p className="text-xs text-destructive" data-testid="profile-adult-required" role="status">
              {t("profile.adultRequired")}
            </p>
          )}
        </div>
        )}
        <div className="space-y-2">
          <Label>{t("profile.city")}</Label>
          {account && !changingCity ? (
            <div className="flex flex-wrap items-center gap-3" data-testid="profile-city-text">
              <span className="text-sm">{cityName}</span>
              {tenants.length > 1 && (
                <button type="button" className="text-sm text-[hsl(var(--primary))] underline" data-testid="profile-change-city" onClick={() => setChangingCity(true)}>
                  {t("common.changeCity")}
                </button>
              )}
            </div>
          ) : (
          <div className="flex flex-wrap gap-2" data-testid="profile-city-options">
            {tenants.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid={`profile-city-${c.slug}`}
                className={chip(form.city_tenant_id === c.id)}
                onClick={() => setForm({ ...form, city_tenant_id: c.id })}
              >
                {lang === "hi" ? c.name_hi || c.name : c.name}
              </button>
            ))}
          </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("profile.interests")}</Label>
        <p className="text-xs text-muted-foreground">{t("profile.interestsHint")}</p>
        <div className="flex flex-wrap gap-2" data-testid="profile-interest-options">
          {INTEREST_OPTIONS.map((key) => (
            <button
              key={key}
              type="button"
              data-testid={`profile-interest-${key}`}
              className={chip(form.interests.includes(key))}
              onClick={() => toggleInterest(key)}
            >
              {interestLabels[key]}
            </button>
          ))}
        </div>
      </div>

      {account ? (
        // ONE pinned Save: stays in view at the bottom of the form, says "Saved ✓" when nothing is
        // pending, so there is never a question whether the page is saved.
        <div className="sticky bottom-0 -mx-1 border-t border-border bg-background/95 px-1 py-3 backdrop-blur" data-testid="profile-save-bar">
          <Button className="rounded-full" data-testid="profile-save-btn" disabled={busy || !dirty} onClick={save}>
            {busy ? t("common.saving") : dirty ? submitLabel || t("profile.save") : `${t("account.savedState")} ✓`}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          <Button className="rounded-full" data-testid="profile-save-btn" disabled={busy || !form.is_adult} onClick={save}>
            {busy ? t("common.saving") : submitLabel || t("profile.save")}
          </Button>
          {onSkip && (
            <button className="text-sm text-muted-foreground underline" data-testid="profile-skip-btn" onClick={skip}>
              {t("profile.skip")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
